"use client"
import { useState, useEffect, Component } from "react"
import { useDispatch, useSelector } from "react-redux"
import { logout } from "../store/slices/authSlice"
import { fetchStudents } from "../store/slices/studentSlice"
import { fetchDepartmentFeeSummary } from "../store/slices/feeSlice"
import FeesComponent from "./FeesComponent"
import StudentsComponent from "./StudentsComponent"
import DocumentsComponent from "./DocumentsComponent"

import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  Users,
  LogOut,
  GraduationCap,
  FolderOpen,
  BarChart3,
  UserCheck,
  Hourglass,
  MenuIcon,
  IndianRupee,
} from "lucide-react"

const PRIMARY_COLOR = "#3f51b5"
const ACCENT_COLOR = "#00c853"
const WARNING_COLOR = "#ff9800"
const DANGER_COLOR = "#e53935"
const BG_COLOR = "#f4f6f8"

const FacultyDashboard = () => {
  const dispatch = useDispatch()
  const { user, loading: authLoading } = useSelector((state) => state.auth)
  const { students, loading: studentsLoading } = useSelector((state) => state.students)
  const { departmentSummary, loadingDepartment: departmentLoading } = useSelector((state) => state.fees)
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("facultyActiveTab") || "overview"
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  class ErrorBoundary extends Component {
    state = { hasError: false }
    static getDerivedStateFromError() {
      return { hasError: true }
    }
    render() {
      if (this.state.hasError) {
        return (
          <div className="text-red-500 text-center p-4 bg-red-50 rounded-lg m-4">
            Error in **{this.props.componentName}**. Please check the console for details.
          </div>
        )
      }
      return this.props.children
    }
  }

  const handleLogout = () => {
    localStorage.removeItem("facultyActiveTab")
    setActiveTab("overview")
    dispatch(logout())
  }

  useEffect(() => {
    localStorage.setItem("facultyActiveTab", activeTab)
  }, [activeTab])

  useEffect(() => {
    dispatch(fetchStudents())
    dispatch(fetchDepartmentFeeSummary())
  }, [dispatch])

  // Faculty can only see students in their department
  const departmentStudents = students.filter((student) => student.department_id === user?.department_id)

  const tabs = [
    { id: "overview", label: "Dashboard", icon: BarChart3 },
    { id: "students", label: "My Students", icon: Users },
    // { id: "fees", label: "Fees & Finance", icon: CreditCard },
    { id: "documents", label: "Documents", icon: FolderOpen },
  ]

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const renderOverview = () => {
    const totalStudents = departmentStudents.length
    const enrolledStudents = departmentStudents.filter((s) => s.admission_status === "Enrolled").length
    const pendingStudents = departmentStudents.filter((s) => s.admission_status === null).length
    const approvedStudents = departmentStudents.filter((s) => s.admission_status === "Approved").length

    const formatINR = (val) =>
      new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(
        Number(val || 0),
      )

    const statsCards = [
      {
        label: "Total Institute Students",
        value: totalStudents,
        icon: <Users size={32} className="text-white" />,
        color: PRIMARY_COLOR,
      },
      {
        label: "Enrolled Students",
        value: enrolledStudents,
        icon: <UserCheck size={32} className="text-white" />,
        color: ACCENT_COLOR,
      },
      /* {
        label: "Approved & Awaiting Enrollment",
        value: approvedStudents,
        icon: <GraduationCap size={32} className="text-white" />,
        color: PRIMARY_COLOR,
      }, */
      {
        label: "Pending Review",
        value: pendingStudents,
        icon: <Hourglass size={32} className="text-white" />,
        color: WARNING_COLOR,
      },
      {
        label: "Institute Due Amount",
        value: departmentLoading || authLoading ? "..." : formatINR(departmentSummary?.total_pending_amount || 0),
        icon: <IndianRupee size={32} className="text-white" />,
        color: DANGER_COLOR,
      },
    ]

    return (
      <div className="flex flex-col gap-8">
        {/* Title Section */}
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 mb-1">Faculty Dashboard</h1>
          <p className="text-gray-600 font-medium">
            Welcome back, <span className="text-gray-900 font-bold">{user?.name}</span>. Here is the overview of the{" "}
            <span className="text-blue-700 font-bold">{user?.department_name || "Assigned"}</span> Institute.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {studentsLoading || authLoading ? (
            <div className="col-span-full text-center">
              <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
              <p className="text-gray-600">Loading Student Stats...</p>
            </div>
          ) : (
            statsCards.map((card, idx) => (
              <div
                key={idx}
                className="bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition duration-300 transform hover:-translate-y-0.5 flex items-center justify-between"
              >
                <div className="flex flex-col">
                  <p className="text-sm font-medium text-gray-500 uppercase">{card.label}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center p-3 shadow-md"
                  style={{ backgroundColor: card.color }}
                >
                  {card.icon}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Department Info & Quick Actions Card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            className="bg-white p-6 rounded-xl shadow-lg flex justify-between items-center border-l-4"
            style={{ borderColor: PRIMARY_COLOR }}
          >
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Your Profile Information</h3>
              <p className="font-semibold text-gray-900">{user?.role === "hod" ? "HOD member" : "Faculty member"}</p>
              <p className="text-sm text-gray-500">
                Institute: <span className="font-medium text-gray-800">{user?.department_name || "Not Assigned"}</span>
              </p>
              <p className="text-sm text-gray-500">
                Faculty ID: <span className="font-medium text-gray-800">{user?.faculty_id || user?.id}</span>
              </p>
            </div>
            <GraduationCap size={40} color={PRIMARY_COLOR} />
          </div>

          <div className="bg-white p-6 rounded-xl shadow-lg border-l-4" style={{ borderColor: ACCENT_COLOR }}>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Student Management</h3>
            <p className="text-sm text-gray-600 mb-4">Quickly access and manage applications pending review.</p>
            <Button
              variant="contained"
              onClick={() => setActiveTab("students")}
              sx={{
                textTransform: "none",
                bgcolor: ACCENT_COLOR,
                "&:hover": { bgcolor: "#1e7e34" },
                borderRadius: 2,
                mt: 1,
              }}
            >
              Go to My Students
            </Button>
            {departmentSummary && !departmentLoading && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Pending Student</p>
                  <p className="text-lg font-bold text-gray-900">{pendingStudents || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Due</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatINR(departmentSummary.total_pending_amount || 0)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderTabContent = () => {
    if (studentsLoading || authLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <CircularProgress sx={{ color: PRIMARY_COLOR }} />
        </div>
      )
    }

    switch (activeTab) {
      case "overview":
        return renderOverview()
      case "students":
        // Pass faculty restrictions to StudentsComponent
        return <StudentsComponent facultyMode={true} departmentId={user?.department_id} />
      case "fees":
        return (
          <ErrorBoundary componentName="FeesComponent">
            <FeesComponent />
          </ErrorBoundary>
        )
      case "documents":
        return (
          <ErrorBoundary componentName="DocumentsComponent">
            <DocumentsComponent />
          </ErrorBoundary>
        )
      default:
        return renderOverview()
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
          <p className="text-gray-600">Loading Faculty Dashboard...</p>
        </div>
      </div>
    )
  }

  const drawerContent = (
    <div className="p-4 h-full flex flex-col justify-between">
      <List>
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isSelected = activeTab === tab.id
          return (
            <ListItem key={tab.id} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  setActiveTab(tab.id)
                  if (isMobile) setMobileOpen(false)
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  "&.Mui-selected": {
                    bgcolor: PRIMARY_COLOR,
                    color: "#fff",
                    boxShadow: "0 4px 6px rgba(0, 123, 255, 0.3)",
                    "&:hover": { bgcolor: "#0056b3" },
                  },
                  "&:hover": { bgcolor: "#f0f8ff" },
                  transition: "all 0.2s",
                }}
              >
                <ListItemIcon>
                  <Icon size={20} color={isSelected ? "#fff" : PRIMARY_COLOR} />
                </ListItemIcon>
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? "bold" : "normal",
                    color: isSelected ? "#fff" : "#333",
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>

      <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
        <Button
          fullWidth
          onClick={handleLogout}
          startIcon={<LogOut size={16} />}
          variant="contained"
          sx={{
            textTransform: "none",
            bgcolor: DANGER_COLOR,
            "&:hover": { bgcolor: "#a71d2a" },
            borderRadius: 2,
          }}
        >
          Logout
        </Button>
      </Box>
    </div>
  )

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: BG_COLOR }}>
      {/* Header */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#fff",
          borderBottom: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: 3, py: 1 }}>
          <div className="flex items-center gap-2 flex-grow">
            {isMobile && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ color: PRIMARY_COLOR }}>
                <MenuIcon />
              </IconButton>
            )}
            <GraduationCap size={28} color={PRIMARY_COLOR} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#333" }}>
              FACULTY PORTAL
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: PRIMARY_COLOR, fontWeight: "medium" }}>
                {user?.role === "hod" ? "HOD member" : "Faculty Member"}
              </Typography>
            </div>
            {!isMobile && (
              <Button
                onClick={handleLogout}
                startIcon={<LogOut size={18} />}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: DANGER_COLOR,
                  color: DANGER_COLOR,
                  "&:hover": {
                    bgcolor: DANGER_COLOR,
                    color: "#fff",
                    borderColor: DANGER_COLOR,
                  },
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Logout
              </Button>
            )}
          </div>
        </Toolbar>
      </AppBar>

      {/* Drawer (Sidebar) */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: 240,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: 240,
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
            top: 64,
            height: "calc(100% - 64px)",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content Area */}
      <main
        className="flex-grow p-8 transition-all duration-300"
        style={{ marginTop: 64 }} // Removed explicit marginLeft and width; rely on flex-grow
      >
        {renderTabContent()}
      </main>
    </div>
  )
}

export default FacultyDashboard
